import { useMutation, useQuery } from '@tanstack/react-query';

import { getCategories } from '~apis/cash-flow-overview/getCategories';
import {
  addCompanySpecificCategories,
  deleteCompanySpecificCategories,
  editCompanySpecificCategories,
  getCategoryTree,
  getCategoryUsedCounts,
  moveParentCategory,
  reorderCompanySpecificCategories,
  toggleActiveCompanySpecificCategories,
} from '~apis/categories';
import { RuleQueryKey } from '~hooks/queries/rule/query';
import { queryClient } from '~libs/queryClient';

import { AnalysisQueryKey } from '../analysis/query';

interface CompanyIdentifier {
  companyId: string;
}

export const CategoriesQueryKey = {
  default: () => ['categories'],
  list: ({ companyId }: CompanyIdentifier) => [...CategoriesQueryKey.default(), 'list', companyId],
  tree: ({ companyId }: CompanyIdentifier) => [...CategoriesQueryKey.default(), 'tree', companyId],
  usedCounts: ({
    companyId,
    categoryId,
    id,
    includeChildren,
  }: CompanyIdentifier & {
    categoryId: number;
    id: number;
    includeChildren?: boolean;
  }) => [...CategoriesQueryKey.default(), 'usedCounts', companyId, categoryId, id, includeChildren],
};

export function useCategories(identifier: CompanyIdentifier, refetchOnMount = false) {
  return useQuery({
    queryKey: CategoriesQueryKey.list(identifier),
    queryFn: () => getCategories(identifier),
    enabled: !!identifier.companyId,
    refetchOnMount,
  });
}

export function useCategoriesTree(identifier: CompanyIdentifier, refetchOnMount?: boolean) {
  return useQuery({
    queryKey: CategoriesQueryKey.tree(identifier),
    queryFn: () => getCategoryTree(identifier),
    enabled: !!identifier.companyId,
    refetchOnMount: refetchOnMount ?? true,
  });
}

export function useCategoryUsedCounts({
  companyId,
  categoryId,
  id,
  includeChildren,
}: {
  companyId: string;
  categoryId: number;
  id: number;
  includeChildren?: boolean;
}) {
  return useQuery({
    enabled: !!companyId && !!categoryId && !!id,
    queryKey: CategoriesQueryKey.usedCounts({ companyId, categoryId, id, includeChildren }),
    queryFn: () => getCategoryUsedCounts({ companyId, categoryId, id, includeChildren }),
    refetchOnMount: true,
  });
}

export function useAddCompanySpecificCategories() {
  return useMutation({
    mutationFn: addCompanySpecificCategories,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(CategoriesQueryKey.list(variables));
      queryClient.invalidateQueries(CategoriesQueryKey.tree(variables));
      queryClient.invalidateQueries(AnalysisQueryKey.default());
    },
  });
}

export function useEditCompanySpecificCategories() {
  return useMutation({
    mutationFn: editCompanySpecificCategories,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(CategoriesQueryKey.list(variables));
      queryClient.invalidateQueries(CategoriesQueryKey.tree(variables));
      queryClient.invalidateQueries(AnalysisQueryKey.default());
    },
  });
}

export function useDeleteCompanySpecificCategories() {
  return useMutation({
    mutationFn: deleteCompanySpecificCategories,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(CategoriesQueryKey.list(variables));
      queryClient.invalidateQueries(RuleQueryKey.default(variables.companyId));
      queryClient.invalidateQueries(CategoriesQueryKey.tree(variables));
    },
  });
}

export function useReorderCompanySpecificCategories() {
  return useMutation({
    mutationFn: reorderCompanySpecificCategories,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(CategoriesQueryKey.list(variables));
      queryClient.invalidateQueries(CategoriesQueryKey.tree(variables));
    },
  });
}

export function useToggleActiveCategory() {
  return useMutation({
    mutationFn: toggleActiveCompanySpecificCategories,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(CategoriesQueryKey.tree(variables));
    },
  });
}

export function useMoveParentCategory() {
  return useMutation({
    mutationFn: moveParentCategory,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(CategoriesQueryKey.tree(variables));
    },
  });
}
